import { transformParamsToCrudApi } from '@app/core/crud/listing-adapter';
import { convertGetManyResponse } from '@app/core/crud/response-converter';
import { useTimezone } from '@app/core/hooks/useTimezone';
import { ListViewInfinite } from '@app/core/list-view/ListViewInfinite';
import { ListViewQueryParams } from '@app/core/list-view/types';
import { Station } from '@app/core/types/stations';
import { EnrichedCompositionListItemDto } from '@app/modules/compose/composition.types';
import { webAppListCompositions } from '@mi-front/api-v2';
import { Paginated, cleanQuery } from '@mi-front/core';
import { CompositionsGrid } from './compositions-grid/CompositionsGrid';
import { CompositionsListEmptyState } from './compositions-list-empty-state/CompositionsListEmptyState';
import { CreateCompositionButton } from './CreateCompositionButton';
import { COMPOSE_FILTERS_CONFIG } from './filters/compose-filters-config';

/**
 * Station-scoped compositions list using BFF pagination (`page`, `limit`, optional `start`/`end`/`name`).
 */
export function ComposeList({ station }: { station: Station }) {
  const timezone = useTimezone();
  const listId = `compose-compositions-list-${station.slug}`;

  const queryFn = async (
    params: ListViewQueryParams,
  ): Promise<Paginated<EnrichedCompositionListItemDto>> => {
    const processedParams = transformParamsToCrudApi(params, {
      filterDefinitions: COMPOSE_FILTERS_CONFIG,
      context: { timezone },
    });

    // BFF accepts flat query params only — drop CRUD `filter` / `sort` from the adapter output.
    const { filter: _filter, sort: _sort, format: _format, ...apiParams } = processedParams;

    const response = await webAppListCompositions(
      cleanQuery({
        stationId: station.id,
        ...apiParams,
      }),
    );

    return await convertGetManyResponse(response);
  };

  return (
    <ListViewInfinite
      listId={listId}
      filters={COMPOSE_FILTERS_CONFIG}
      queryFn={queryFn}
      actions={[<CreateCompositionButton key="create-composition" stationSlug={station.slug} />]}
      ListComponent={CompositionsGrid}
      renderEmptyState={() => <CompositionsListEmptyState stationSlug={station.slug} />}
      showProgress
      maxPages={3}
    />
  );
}
